import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import axios from 'axios';
import { isExpired, decodeToken } from 'react-jwt';
import { FaBuilding, FaSearch, FaArrowRight, FaCheckCircle, FaExclamationTriangle, FaArrowLeft } from 'react-icons/fa';
import Header from '../others/Header';
import config from '../config/config';
import { useToast } from '../components/ToastContext';
import { nomeDaEmpresa, formatarCnpj, filtrarEmpresas, situacaoDaEmpresa } from '../others/selecaoEmpresa';

// Seleção de empresa do MANAGER: logo após o login (e pelo menu "Trocar Empresa" da Home) ele escolhe com qual
// empresa trabalhar e passa a ver só os dados dela. A troca devolve um token novo, já com os dados da empresa,
// então ele segue direto para a Home, sem precisar entrar de novo.
//
// GET /acesso-empresa/opcoes      -> empresas para escolher (a atual vem marcada com "atual")
// PUT /acesso-empresa/{idEmpresa} -> grava a escolha e devolve { token }

const getAuthHeader = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('jwtToken')}` } });

function SelecionarEmpresa() {
  const { API_BASE_URL } = config;
  const { addToast } = useToast();

  const [podeExibir, setPodeExibir] = useState(false);
  const [empresas, setEmpresas] = useState([]);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState('');
  const [busca, setBusca] = useState('');
  const [entrandoEm, setEntrandoEm] = useState(null);

  // Só o MANAGER usa esta tela. Com senha provisória, a Home é quem obriga a troca primeiro (a API recusa o resto).
  useEffect(() => {
    const token = localStorage.getItem('jwtToken');
    if (!token || isExpired(token)) {
      window.location.href = '/login';
      return;
    }
    const dados = decodeToken(token);
    if (dados?.tipoUsuario !== 'MANAGER' || dados?.needUpdatePass) {
      window.location.href = '/home';
      return;
    }
    setPodeExibir(true);
  }, []);

  useEffect(() => {
    if (!podeExibir) return;
    const carregar = async () => {
      try {
        const { data } = await axios.get(`${API_BASE_URL}/acesso-empresa/opcoes`, getAuthHeader());
        setEmpresas(data || []);
      } catch (error) {
        console.error('Erro ao carregar empresas:', error);
        setErro(error.response?.data?.message || 'Não foi possível carregar as empresas.');
      } finally {
        setCarregando(false);
      }
    };
    carregar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [podeExibir]);

  const entrar = async (empresa) => {
    setEntrandoEm(empresa.idEmpresa);
    try {
      const { data } = await axios.put(`${API_BASE_URL}/acesso-empresa/${empresa.idEmpresa}`, null, getAuthHeader());
      localStorage.setItem('jwtToken', data.token);
      window.location.href = '/home';
    } catch (error) {
      console.error('Erro ao entrar na empresa:', error);
      addToast('error', error.response?.data?.message || 'Não foi possível entrar nesta empresa.');
      setEntrandoEm(null);
    }
  };

  if (!podeExibir) return null;

  const atual = empresas.find((e) => e.atual);
  const lista = filtrarEmpresas(empresas, busca);

  return (
    <PageBackground>
      <Header />
      <Content>
        <TitleRow>
          <Title><FaBuilding size={22} /> Selecionar empresa</Title>
          {atual && (
            <SecondaryButton type="button" onClick={() => { window.location.href = '/home'; }} disabled={!!entrandoEm}>
              <FaArrowLeft /> Continuar em {nomeDaEmpresa(atual)}
            </SecondaryButton>
          )}
        </TitleRow>

        <Card>
          <Intro>
            Como Manager, você pode acessar qualquer empresa cadastrada. Escolha com qual vai trabalhar agora:
            todas as telas passam a mostrar somente os dados dela. Para trocar depois, use <strong>Trocar Empresa</strong> no menu.
          </Intro>

          <BuscaWrapper>
            <FaSearch />
            <Input
              type="text"
              value={busca}
              onChange={(e) => setBusca(e.target.value)}
              placeholder="Buscar por nome, razão social, cidade ou CNPJ..."
              autoFocus
            />
          </BuscaWrapper>

          {carregando ? (
            <EmptyState>Carregando empresas...</EmptyState>
          ) : erro ? (
            <EmptyState style={{ color: '#e74c3c' }}>{erro}</EmptyState>
          ) : lista.length === 0 ? (
            <EmptyState>{busca ? 'Nenhuma empresa encontrada para esta busca.' : 'Nenhuma empresa cadastrada.'}</EmptyState>
          ) : (
            <Lista>
              {lista.map((empresa) => {
                const situacao = situacaoDaEmpresa(empresa);
                const entrando = entrandoEm === empresa.idEmpresa;
                return (
                  <Item
                    key={empresa.idEmpresa}
                    type="button"
                    $atual={empresa.atual}
                    onClick={() => entrar(empresa)}
                    disabled={!!entrandoEm}
                    title={`Entrar em ${nomeDaEmpresa(empresa)}`}
                  >
                    <IconeEmpresa $atual={empresa.atual}><FaBuilding /></IconeEmpresa>
                    <div className="info">
                      <strong>{nomeDaEmpresa(empresa)}</strong>
                      <span>
                        {[
                          empresa.nomeFantasia && empresa.razaoSocial && empresa.razaoSocial !== empresa.nomeFantasia ? empresa.razaoSocial : null,
                          empresa.cnpj ? formatarCnpj(empresa.cnpj) : null,
                          empresa.cidade || null,
                        ].filter(Boolean).join(' · ')}
                      </span>
                    </div>
                    <div className="selos">
                      {empresa.atual && <Selo $cor="#008080"><FaCheckCircle /> Atual</Selo>}
                      {situacao && <Selo $cor="#d68910"><FaExclamationTriangle /> {situacao}</Selo>}
                    </div>
                    <span className="acao">{entrando ? 'Entrando...' : <>Entrar <FaArrowRight /></>}</span>
                  </Item>
                );
              })}
            </Lista>
          )}

          {!carregando && !erro && empresas.length > 0 && (
            <HintText>
              {empresas.length} empresa(s) cadastrada(s). Empresas desativadas ou com período vencido também podem ser
              acessadas por você, Manager; os usuários delas continuam bloqueados.
            </HintText>
          )}
        </Card>
      </Content>
    </PageBackground>
  );
}

const PageBackground = styled.div`
  min-height: 100vh;
  background: linear-gradient(135deg, #e6f7f5 0%, #d4efed 100%);
`;

const Content = styled.div`
  max-width: 900px;
  margin: 0 auto;
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 18px;

  @media (max-width: 600px) { padding: 16px; }
`;

const TitleRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 12px;
`;

const Title = styled.h1`
  display: flex;
  align-items: center;
  gap: 10px;
  color: #004d4d;
  font-size: 1.4rem;
  font-weight: 700;
  margin: 0;
`;

const Card = styled.div`
  background: white;
  border-radius: 12px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.08);
  border: 1px solid rgba(0, 128, 128, 0.15);
  padding: 20px;
`;

const HintText = styled.span`
  display: block;
  color: #7f8c8d;
  font-size: 11.5px;
  margin-top: 4px;
  font-style: italic;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 20px;
  color: #7f8c8d;
  font-size: 13px;
`;

const Input = styled.input`
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #ddd;
  border-radius: 8px;
  font-size: 14px;
  box-sizing: border-box;
  &:focus {
    outline: none;
    border-color: #008080;
    box-shadow: 0 0 0 3px rgba(0, 128, 128, 0.1);
  }
`;

const SecondaryButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 22px;
  background: #f1f3f5;
  color: #34495e;
  border: 1px solid #dee2e6;
  border-radius: 10px;
  font-weight: 700;
  font-size: 14px;
  cursor: pointer;
  &:hover:not(:disabled) { background: #e9ecef; }
  &:disabled { cursor: wait; opacity: 0.7; }
`;

const Intro = styled.p`
  margin: 0 0 16px 0;
  color: #34495e;
  font-size: 14px;
  line-height: 1.5;
`;

const BuscaWrapper = styled.div`
  position: relative;
  margin-bottom: 14px;
  svg {
    position: absolute;
    left: 12px;
    top: 50%;
    transform: translateY(-50%);
    color: #95a5a6;
    font-size: 13px;
  }
  input { padding-left: 34px; }
`;

const Lista = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  max-height: 60vh;
  overflow-y: auto;
  margin-bottom: 8px;
`;

const Item = styled.button`
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  padding: 12px 14px;
  text-align: left;
  font: inherit;
  background: ${(p) => (p.$atual ? '#f0fdfa' : 'white')};
  border: 1px solid ${(p) => (p.$atual ? 'rgba(0,128,128,0.45)' : '#e3e8e8')};
  border-radius: 10px;
  cursor: pointer;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;

  &:hover:not(:disabled) {
    border-color: #008080;
    box-shadow: 0 3px 10px rgba(0,128,128,0.15);
  }
  &:disabled { cursor: wait; opacity: 0.7; }

  .info { display: flex; flex-direction: column; flex: 1; min-width: 0; }
  .info strong { font-size: 14.5px; color: #004d4d; word-break: break-word; }
  .info span { font-size: 12.5px; color: #7f8c8d; margin-top: 2px; word-break: break-word; }
  .selos { display: flex; gap: 6px; flex-wrap: wrap; justify-content: flex-end; }
  .acao {
    display: flex;
    align-items: center;
    gap: 6px;
    flex-shrink: 0;
    color: #008080;
    font-weight: 700;
    font-size: 13px;
  }

  @media (max-width: 600px) {
    flex-wrap: wrap;
    .selos { order: 3; width: 100%; justify-content: flex-start; }
  }
`;

const IconeEmpresa = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  flex-shrink: 0;
  border-radius: 10px;
  background: ${(p) => (p.$atual ? '#008080' : '#eaf6f6')};
  color: ${(p) => (p.$atual ? 'white' : '#008080')};
`;

const Selo = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 2px 8px;
  border-radius: 10px;
  font-size: 11px;
  font-weight: 700;
  white-space: nowrap;
  color: ${(p) => p.$cor};
  background: ${(p) => `${p.$cor}1a`};
`;

export default SelecionarEmpresa;
